import { useMemo, useRef, useState } from "react";
import { FolderUp, FileSpreadsheet, Check, Home, Plus, Search, UploadCloud, X } from "lucide-react";
import ErrorDetailsDialog from "./ErrorDetailsDialog";
import { useTakeoff } from "./store";
import { downloadEstimateXlsx } from "./api";
import { BTN_HEAD, MONO, ACCEPT_EXT } from "./theme";

const NAV = [
  { key: "home", label: "Home", icon: true },
  { key: "plans", label: "Plans & Takeoffs" },
  { key: "estimate", label: "Estimate" },
];

const SaveDot = ({ state }) => (
  <span className="flex items-center gap-1.5 text-[11px] font-medium" data-testid="takeoff-save-status">
    <span className={`h-2 w-2 rounded-full ${state === "saving" ? "animate-pulse bg-amber-400" : state === "error" ? "bg-red-400" : "bg-emerald-400"}`} />
    <span className="text-slate-500 dark:text-white/60">{state === "saving" ? "Saving…" : state === "error" ? "Cloud error" : "Saved"}</span>
  </span>
);

/**
 * Slim module tab strip matching the app's own tab bar: home icon +
 * Plans & Takeoffs / Estimate tabs, then project selector, New Job /
 * Find Jobs, upload progress, save status, project status, Upload folder
 * and Export Excel (outside Home).
 */
export default function ModuleHeader({ view, onNavigate, contextLine, onNewJob, onFindJobs, exportScope }) {
  const {
    workspace, activeId, setActiveId, activeProject, statuses, updateProject,
    saveState, uploads, dismissUpload, uploadFiles, retryUpload, uploadBatch,
    viewer,
  } = useTakeoff();
  const folderInput = useRef(null);
  const filesInput = useRef(null);
  const resumeInput = useRef(null);
  const [resumeKey, setResumeKey] = useState(null);
  const [errDetails, setErrDetails] = useState(null);
  const projects = useMemo(
    () => [...(workspace?.projects || [])].sort((a, b) => (b.created_at || 0) - (a.created_at || 0)),
    [workspace]
  );
  const activeUpload = uploads.find((u) => !u.error && u.status !== "ready" && u.status !== "paused"); // paused rows live in the Plans panel with Resume
  const readyUploads = uploads.filter((u) => !u.error && u.status === "ready");
  const failedUploads = uploads.filter((u) => u.error);

  return (
    <div
      data-testid="takeoff-header"
      className="relative z-20 flex min-h-11 flex-wrap items-center gap-x-2 gap-y-1 border-b border-black/10 bg-white px-2 py-1 text-slate-900 sm:px-4 dark:border-white/10 dark:bg-[#0a0a0a] dark:text-white"
    >
      <nav className="flex items-center gap-0.5" aria-label="Takeoff views">
        {NAV.filter((n) => !viewer || n.key !== "estimate").map((n) => {
          const active = view === n.key;
          const cls = `shrink-0 rounded-none border-b-2 px-2 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors sm:px-3 ${
            active
              ? "border-[#1a3fd1] text-[#1a3fd1] dark:text-[#4f6fe8]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-white/50 dark:hover:text-white"
          }`;
          return (
            <button
              key={n.key}
              onClick={() => onNavigate(n.key)}
              data-testid={`takeoff-nav-${n.key}`}
              aria-pressed={active}
              aria-label={n.icon ? "Home" : undefined}
              title={n.icon ? "Home" : undefined}
              className={cls}
            >
              {n.icon ? <Home className="h-4 w-4" /> : n.label}
            </button>
          );
        })}
      </nav>
      {contextLine && (
        <p className="hidden max-w-[220px] truncate font-mono text-[9px] uppercase tracking-widest text-slate-400 md:block dark:text-white/40" data-testid="takeoff-context-line">
          {contextLine}
        </p>
      )}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <select
          value={activeId || ""}
          onChange={(e) => setActiveId(e.target.value)}
          data-testid="takeoff-project-selector"
          aria-label="Active project"
          className="h-8 max-w-[200px] rounded-md border border-black/15 bg-black/[0.04] px-2 text-xs text-slate-800 focus:border-[#f97316] focus:outline-none dark:border-white/15 dark:bg-white/5 dark:text-white [&>option]:text-slate-900"
        >
          <option value="">Select job…</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </select>
        {!viewer && <button className={BTN_HEAD} onClick={onNewJob} data-testid="takeoff-new-job-btn"><Plus className="h-3.5 w-3.5" /> New Job</button>}
        <button className={BTN_HEAD} onClick={onFindJobs} data-testid="takeoff-find-jobs-btn"><Search className="h-3.5 w-3.5" /> Find Jobs</button>

        {activeUpload && (
          <span className="flex items-center gap-2 rounded-md bg-black/5 px-2 py-1 text-[11px] text-slate-700 dark:bg-white/10 dark:text-white/80" data-testid="takeoff-upload-progress">
            <UploadCloud className="h-3.5 w-3.5 animate-pulse text-[#fb923c]" />
            <span className="max-w-[140px] truncate">{activeUpload.name}</span>
            <span className={MONO}>
              {activeUpload.status === "processing" ? "Processing…" : activeUpload.info?.verifying ? "Verifying…" : `${activeUpload.pct}%`}
              {uploadBatch && uploadBatch.total > 1 ? ` · ${uploadBatch.done}/${uploadBatch.total} done` : ""}
              {activeUpload.status !== "processing" && !activeUpload.info?.verifying && activeUpload.info?.speedBps ? ` · ${(activeUpload.info.speedBps / 1048576).toFixed(1)} MB/s` : ""}
              {activeUpload.status !== "processing" && !activeUpload.info?.verifying && activeUpload.info?.etaText ? ` · ${activeUpload.info.etaText}` : ""}
              {activeUpload.info?.chunk ? ` · part ${activeUpload.info.chunk}/${activeUpload.info.chunkCount}` : ""}
              {activeUpload.info?.retries ? ` · retry ${activeUpload.info.retries}` : ""}
            </span>
          </span>
        )}
        {readyUploads.map((u) => (
          <span key={u.key} className="flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" data-testid="takeoff-upload-ready">
            <Check className="h-3.5 w-3.5" />
            <span className="max-w-[160px] truncate">{u.name}</span>
            <span className="font-semibold">Ready</span>
            <button aria-label={`Dismiss ${u.name}`} onClick={() => dismissUpload(u.key)} className="rounded p-0.5 hover:bg-black/10 dark:hover:bg-white/10"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {failedUploads.map((u) => (
          <span key={u.key} className="flex items-center gap-2 rounded-md bg-red-500/10 px-2 py-1 text-[11px] text-red-700 dark:bg-red-500/15 dark:text-red-300" data-testid="takeoff-upload-error">
            <span className="max-w-[220px] truncate">{u.error}</span>
            {u.errorMeta && (
              <button aria-label={`View error details for ${u.name}`} onClick={() => setErrDetails(u.errorMeta)} className="rounded px-1 py-0.5 font-semibold underline hover:bg-black/10 dark:hover:bg-white/10" data-testid="takeoff-upload-details">Details</button>
            )}
            <button
              aria-label={`Retry uploading ${u.name}`}
              title={u.file ? "Resume from the last confirmed chunk" : "Re-select the same file — confirmed chunks are kept, it will not restart at zero"}
              onClick={() => { if (u.file) retryUpload(u.key); else { setResumeKey(u.key); resumeInput.current?.click(); } }}
              className="rounded px-1 py-0.5 font-semibold hover:bg-black/10 dark:hover:bg-white/10" data-testid={`takeoff-upload-retry`}>
              {u.file ? "Resume" : "Re-select & resume"}
            </button>
            <button aria-label="Dismiss upload error" onClick={() => dismissUpload(u.key)} className="rounded p-0.5 hover:bg-black/10 dark:hover:bg-white/10"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {errDetails && (
          <ErrorDetailsDialog
            error={errDetails}
            onClose={() => setErrDetails(null)}
            onRetry={errDetails.uploadId ? () => {
              const u = uploads.find((x) => x.errorMeta === errDetails);
              setErrDetails(null);
              if (!u) return;
              if (u.file) retryUpload(u.key);
              else { setResumeKey(u.key); resumeInput.current?.click(); }
            } : null}
          />
        )}
        <input
          ref={resumeInput} type="file" className="hidden" data-testid="takeoff-resume-input"
          onChange={(e) => { const f = e.target.files?.[0]; if (f && resumeKey) retryUpload(resumeKey, f); e.target.value = ""; setResumeKey(null); }}
        />

        <SaveDot state={saveState} />

        {/* Plain file upload (incl. .zip — archives auto-unpack into the job
            server-side). Available on every view once a job is active; the
            folder picker can't select zip files, which made zips feel
            blocked. No category passed so each file self-classifies. */}
        {activeProject && !viewer && (
          <>
            <button className={BTN_HEAD} onClick={() => filesInput.current?.click()} data-testid="takeoff-upload-files-btn">
              <UploadCloud className="h-3.5 w-3.5" /> Upload files
            </button>
            <input
              ref={filesInput}
              type="file"
              multiple
              accept={ACCEPT_EXT}
              className="hidden"
              data-testid="takeoff-upload-files-input"
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
            />
          </>
        )}

        {activeProject && !viewer && (
          <select
            value={activeProject.status || "Not Set"}
            onChange={(e) => updateProject(activeProject.project_id, { status: e.target.value })}
            data-testid="takeoff-status-selector"
            aria-label="Project status"
            className="h-8 rounded-md border border-black/15 bg-black/[0.04] px-2 text-xs text-slate-800 focus:border-[#f97316] focus:outline-none dark:border-white/15 dark:bg-white/5 dark:text-white [&>option]:text-slate-900"
          >
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        {view !== "home" && activeProject && !viewer && (
          <>
            <button className={BTN_HEAD} onClick={() => folderInput.current?.click()} data-testid="takeoff-upload-folder-btn">
              <FolderUp className="h-3.5 w-3.5" /> Upload folder
            </button>
            <input
              ref={folderInput}
              type="file"
              multiple
              className="hidden"
              data-testid="takeoff-upload-folder-input"
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
              {...{ webkitdirectory: "", directory: "" }}
            />
            <button
              className={BTN_HEAD}
              onClick={() => downloadEstimateXlsx(activeProject.project_id, activeProject.name, exportScope || {})}
              data-testid="takeoff-export-excel-btn"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" /> Export Excel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
